import { X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ── helpers ───────────────────────────────────────────────────────────────────

function safeCurrencyCode(currency) {
  const n = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(n) ? n : "USD";
}

function fmtMoney(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: safeCurrencyCode(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const fmtRatio = (v) => (v == null ? "—" : Number(v).toFixed(2) + "×");

function fmtIrr(irr, reliable) {
  if (!reliable || irr == null) return null;
  return `${(Number(irr) * 100).toFixed(1)}%`;
}

/**
 * NAV/Inv = nav / capital_contributed_cumulative (RVPI).
 * "—" when invested <= 0 or nav is null.
 */
function fmtNavInv(nav, invested) {
  if (nav == null || invested == null || Number(invested) <= 0) return "—";
  return `${(Number(nav) / Number(invested)).toFixed(2)}×`;
}

// ── CompareSheet ──────────────────────────────────────────────────────────────

/**
 * CompareSheet — slide-over panel that compares selected member rows.
 *
 * Props:
 *   open            boolean
 *   onOpenChange    (open: boolean) => void
 *   compareMembers  Member[]  — the full member objects to compare (up to ~10)
 *   onRemove(key)   (key: string) => void — "portal|fund_name|investor_name"
 */
export default function CompareSheet({ open, onOpenChange, compareMembers = [], onRemove }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>Compare Positions ({compareMembers.length})</SheetTitle>
          <SheetDescription>
            Side-by-side comparison of selected investor positions. IRR shown only when reliable.
          </SheetDescription>
        </SheetHeader>

        {compareMembers.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">No positions selected.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs min-w-[160px]">Investor</TableHead>
                  <TableHead className="text-xs">Portal</TableHead>
                  <TableHead className="text-xs min-w-[160px]">Fund</TableHead>
                  <TableHead className="text-xs text-right">Commitment</TableHead>
                  <TableHead className="text-xs text-right">Invested</TableHead>
                  <TableHead className="text-xs text-right">Distributed</TableHead>
                  <TableHead className="text-xs text-right">Current Value</TableHead>
                  <TableHead className="text-xs text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="underline decoration-dotted cursor-help">Value/Inv</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs max-w-[200px]">
                          Current Value ÷ Invested (RVPI — residual value to paid-in capital)
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-xs text-right">TVPI</TableHead>
                  <TableHead className="text-xs text-right">DPI</TableHead>
                  <TableHead className="text-xs text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="underline decoration-dotted cursor-help">IRR</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs max-w-[200px]">
                          IRR shown only when there are dated cashflows. Blank = not reliable.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {compareMembers.map((m) => {
                  const key = `${m.portal}|${m.fund_name}|${m.investor_name}`;
                  const currency = m.currency || "USD";
                  // Invested: prefer capital_contributed_cumulative, fall back to capital_called
                  const invested = m.capital_contributed_cumulative ?? m.capital_called_cumulative;
                  const irrDisplay = fmtIrr(m.irr, m.irr_reliable);

                  return (
                    <TableRow key={key} data-testid={`compare-row-${key}`}>
                      <TableCell className="text-xs font-medium">
                        {m.investor_name}
                        {m.is_exited && (
                          <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                            exited
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 capitalize">{m.portal}</TableCell>
                      <TableCell className="text-xs uppercase">{m.fund_name}</TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {fmtMoney(m.commitment_total, currency)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {fmtMoney(invested, currency)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {fmtMoney(m.distributions_cumulative, currency)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {fmtMoney(m.nav, currency)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono text-gray-600">
                        {fmtNavInv(m.nav, invested)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono font-semibold">
                        {fmtRatio(m.tvpi)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {fmtRatio(m.dpi)}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {irrDisplay ?? "—"}
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => onRemove && onRemove(key)}
                          className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                          aria-label={`Remove ${m.investor_name} from compare`}
                          data-testid={`compare-remove-${key}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
